import React, { useEffect, useRef, useState, useMemo } from "react";
import { useSubject } from "../context/SubjectContext";
import { useNavigate } from "react-router-dom";
import { useCenter } from "../context/CenterContext";
import "./CentreSwitcherModal.css";
import PropTypes from "prop-types";

const CentreSwitcherModal = ({ isOpen, onClose }) => {
  const modalRef = useRef(null);
  const radioRefs = useRef({});
  const navigate = useNavigate();
  const { setDashboardCache } = useSubject();

  const {
    centres, // [{ orgId, name, bpid, productGroup }]
    selectedCentreId,
    selectedCenter,
    setSelectedCentreId,
  } = useCenter();

  // Local staged selection; commit only on "Select centre"
  const [pendingCentreId, setPendingCentreId] = useState(selectedCentreId);

  // Reset pending selection when modal opens to current selection
  useEffect(() => {
    if (isOpen) {
      setPendingCentreId(selectedCentreId);
    }
  }, [isOpen, selectedCentreId]);

  const focusableSelectors =
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        // Close without committing selection
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    const trapFocus = (e) => {
      if (!isOpen || !modalRef.current) return;

      const focusableEls =
        modalRef.current.querySelectorAll(focusableSelectors);
      const focusable = Array.from(focusableEls);
      if (focusable.length === 0) return;

      const firstEl = focusable[0];
      const lastEl = focusable[focusable.length - 1];

      if (e.key === "Tab") {
        if (e.shiftKey) {
          if (document.activeElement === firstEl) {
            e.preventDefault();
            lastEl.focus();
          }
        } else {
          if (document.activeElement === lastEl) {
            e.preventDefault();
            firstEl.focus();
          }
        }
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", trapFocus);

      // Focus current selection’s radio (use selectedCentreId for initial focus)
      if (selectedCentreId && radioRefs.current[selectedCentreId]) {
        radioRefs.current[selectedCentreId].focus();
      }
    }

    return () => {
      document.removeEventListener("keydown", trapFocus);
    };
  }, [isOpen, selectedCentreId]);

  useEffect(() => {
    if (centres?.length) {
    }
  }, [centres, selectedCentreId, selectedCenter]);

  const handleCentre = () => {
    // Commit staged selection
    if (pendingCentreId && pendingCentreId !== selectedCentreId) {
      setSelectedCentreId(pendingCentreId);
    }
    onClose();
    navigate("/");
    setDashboardCache({});
  };

  // NEW: locally sorted centres by numeric orgId (minimal addition)
  const sortedCentres = useMemo(() => {
    return [...centres].sort((a, b) => {
      const an = Number(String(a.orgId).trim());
      const bn = Number(String(b.orgId).trim());
      if (Number.isNaN(an) || Number.isNaN(bn)) {
        // Fallback to string/numeric-aware compare
        return String(a.orgId).localeCompare(String(b.orgId), undefined, {
          numeric: true,
        });
      }
      return an - bn; // ascending: smallest (e.g. 1) first
    });
  }, [centres]);

  if (!isOpen) return null;

  return (
    <div className="modal is-active" id="modal-centre-switcher">
      <button
        className="modal-background"
        onClick={onClose}
        aria-label="Close modal" // Add an accessible label
        tabIndex="0"
      >
        {/* Optionally, you can add visual indication here if needed */}
      </button>

      <div className="modal-card" ref={modalRef}>
        <header className="modal-card-head">
          <p className="modal-card-title">Your centres</p>
          <button
            className="delete"
            aria-label="close"
            onClick={onClose}
          ></button>
        </header>
        <section className="modal-card-body">
          <p>Select the centre you wish to switch to</p>
          <ul className="centre-list mt-4">
            {sortedCentres.map((c) => (
              <li key={c.orgId}>
                <label
                  className={`radio ${
                    pendingCentreId === c.orgId ? "selected" : ""
                  }`}
                  role="radio"
                  tabIndex="0"
                  aria-checked={pendingCentreId === c.orgId}
                  aria-label={`Centre ${c.orgId} – ${c.name}`}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" ||
                      e.key === " " ||
                      e.code === "Space"
                    ) {
                      e.preventDefault();
                      // Stage only; do not commit to context yet
                      setPendingCentreId(c.orgId);
                    }
                  }}
                >
                  <input
                    type="radio"
                    name="radio_group"
                    checked={pendingCentreId === c.orgId}
                    onChange={() => {
                      // Stage only; do not commit to context yet
                      setPendingCentreId(c.orgId);
                    }}
                    ref={(el) => (radioRefs.current[c.orgId] = el)}
                    tabIndex={pendingCentreId === c.orgId ? 0 : -1}
                  />
                  {c.orgId} -{" "}
                  <span className="has-text-weight-normal">{c.name}</span>
                </label>
              </li>
            ))}
          </ul>
        </section>
        <footer className="modal-card-foot">
          <button
            className="button is-primary centre-modal-button"
            onClick={handleCentre}
          >
            Select centre
          </button>
          <button className="button" onClick={onClose}>
            Cancel
          </button>
        </footer>
      </div>
    </div>
  );
};

export default CentreSwitcherModal;

CentreSwitcherModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};